import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Mail, Globe, Shield } from "lucide-react";
import logo from "@/assets/logo.png";
import { APP_VERSION } from "@/version";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="dashboard-container">
      <div className="dashboard-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>🌿 Cilantro Alert</h2>
        <Button size="sm" onClick={() => navigate('/')} data-testid="back-home">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      <div className="dashboard-content" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <img 
            src={logo}
            alt="Cilantro Alert Logo" 
            style={{ maxWidth: '160px', width: '100%', margin: '0 auto 2rem', objectFit: 'contain' }}
            data-testid="about-logo"
          />
          <h1 style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem' }}>About Cilantro Alert</h1>
          <p style={{ fontSize: '1.1rem', color: 'hsl(var(--muted-foreground))' }}>{APP_VERSION}</p>
        </div>

        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Our Mission</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))' }}>
              Cilantro Alert is a community-driven platform dedicated to helping people identify products containing cilantro. 
              Whether you have the genetic variation that makes cilantro taste like soap, or you simply prefer to avoid it, 
              we're here to help you make informed choices about the products you buy.
            </p>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))', marginTop: '1rem', fontWeight: 600 }}>
              We don't sell or use your data in any way, we just really hate coriander.
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>1. Scan or Search</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>Use your device camera to scan product barcodes or search manually by barcode number.</p>
              </div>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>2. Check Status</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>Instantly see if a product is cilantro-safe or contains cilantro, based on community data.</p>
              </div>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>3. Contribute</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>Add new products, update status, and share your experiences through reviews.</p>
              </div>
              <div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>4. Trust Admin Verification</h3>
                <p style={{ color: 'hsl(var(--muted-foreground))' }}>Products verified by admins are locked to ensure accuracy and reliability.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card style={{ marginBottom: '2rem' }}>
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Mail className="w-5 h-5" style={{ color: '#4caf50' }} />
                <a href="mailto:rudy@rm-worx.be" style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'none' }}>
                  rudy@rm-worx.be
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Globe className="w-5 h-5" style={{ color: '#4caf50' }} />
                <a href="https://www.rm-worx.be" target="_blank" rel="noopener noreferrer" style={{ color: 'hsl(var(--muted-foreground))', textDecoration: 'none' }}>
                  www.rm-worx.be
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Built by RM-Worx BV</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              This application is proudly developed and maintained by RM-Worx BV. We built this with the sole purpose of providing ourselves (and others) with an easy tool to avoid coriander.
            </p>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
              Why? For some of us coriander tastes disgusting, and nobody (except maybe dictators and serial killers) should have to endure such a foul taste.
            </p>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))' }}>
              So, let's help each other out, fill up this database and keep our mouths as soap free as possible!
            </p>
          </CardContent>
        </Card>

        <Card style={{ marginTop: '2rem' }}>
          <CardHeader>
            <CardTitle>Data Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <p style={{ fontSize: '1rem', lineHeight: '1.6', color: 'hsl(var(--muted-foreground))' }}>
              Part of our product database is sourced from{' '}
              <a href="https://openfoodfacts.org" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>
                Open Food Facts
              </a>
              , the open food products database, made available under the{' '}
              <a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>
                Open Database License (ODbL)
              </a>
              . Product statuses derived from manufacturer ingredient lists are marked as verified.
            </p>
          </CardContent>
        </Card>

        <div style={{ textAlign: 'center', marginTop: '3rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.85rem' }}>
          <p>© 2026 RM-Worx BV. All rights reserved.</p>
          <p style={{ marginTop: '0.35rem' }}>BE 1041.491.473</p>
          <p style={{ marginTop: '0.5rem' }}>Cilantro Alert {APP_VERSION}</p>
          <button
            onClick={() => navigate('/privacy')}
            style={{ marginTop: '0.75rem', background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            <Shield size={14} /> Privacy Policy
          </button>
        </div>
      </div>
    </div>
  );
};

export default About;